/**
 * Cohorts tab — the reusable rosters (COHORT-SCHEDULING-PLAN Phase 4).
 *
 * A cohort is a named set of people; assigning it an orientation is a
 * separate act (the Cohort option on the Orientation Assignment page)
 * that creates one `cohort_assignment` pairing plus one assignment per
 * member. This tab owns the roster lifecycle: create, rename, edit
 * members, archive — and per pairing, the derived status rollup, the
 * per-member detail with derived skip reasons, and Cancel.
 *
 * This file is the CONTAINER: data wiring, awaited reducer handlers
 * (the #76 rule — a module refusal surfaces its actual reason in a
 * toast), gates, and layout. Presentation lives in the sibling
 * components (CreateCohortCard, CohortRow, PairingDetail,
 * EditMembersDrawer, MemberPicker, atoms).
 *
 * Vocabulary rule (locked decision 6): nothing here says "schedule".
 */
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { useMemo, useState } from 'react';
import { useSpacetimeDB } from 'spacetimedb/react';
import { type DbConnection as MinetwinConn } from '@sdk/minetwin';
import { NoProjects } from '@/components/NoProjects';
import { NoSiteMembership } from '@/components/NoSiteMembership';
import { useMySiteMembership } from '@/hooks/useMySiteMembership';
import {
  useCohortEntities,
  type CohortEntityRow,
  type PairingRow,
} from '@/hooks/useCohortEntities';
import { useUsers } from '@/hooks/useUsers';
import { useOrientations } from '@/hooks/useOrientations';
import { useProjects } from '@/hooks/useProjects';
import { useTweaks } from '@/hooks/useTweaks';
import { useGroupRuns, type GroupRunRowData } from '@/hooks/useGroupRuns';
import { isRunLeader } from '@/lib/groupRunEntity';
import { useMineZeroLaunch } from '@/hooks/useMineZeroLaunch';
import { buildObserverLaunchArgs } from '@/services/launcher/launcherArgs';
import { useAuthKit } from '@/contexts/AuthProvider';
import type { Site, Toast } from '@/data/types';
import { CreateCohortCard } from './CreateCohortCard';
import { CohortRow } from './CohortRow';
import { CreateGroupDrawer } from './CreateGroupDrawer';
import { EditMembersDrawer } from './EditMembersDrawer';
import { ArchiveCohortDialog } from './ArchiveCohortDialog';
import { liveCohortWork } from '@/lib/cohortEntity';
import type { GroupRunActions } from './PairingDetail';
import { Empty } from './atoms';

type CohortsProps = { toast: (t: Omit<Toast, 'id'>) => void };

export function Cohorts({ toast }: CohortsProps) {
  const dialogs = useDialogs();
  const minetwinState = useSpacetimeDB('minetwin');
  const { sites, isReady: sitesReady } = useProjects();
  const { tweaks } = useTweaks();
  const currentSite: Site | undefined = sites[tweaks.siteIndex] ?? sites[0];
  const projectId = currentSite?.projectName ?? '';
  const membership = useMySiteMembership(projectId);

  const { cohorts, assignmentSlices, isReady } = useCohortEntities(projectId);
  const { users } = useUsers();
  const { orientations } = useOrientations();
  const orientationNameById = useMemo(
    () => new Map(orientations.map((o) => [o.orientationId, o.displayName])),
    [orientations],
  );
  const userEmails = useMemo(
    () =>
      (projectId
        ? users.filter((u) => u.projectIds.includes(projectId))
        : users
      ).map((u) => u.email),
    [users, projectId],
  );

  const [busyId, setBusyId] = useState<bigint | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editing, setEditing] = useState<CohortEntityRow | null>(null);
  // #191: the three-way archive choice, open only when something is under
  // way on the cohort (the plain case keeps its one-line confirm).
  const [archiving, setArchiving] = useState<{
    cohort: CohortEntityRow;
    liveRuns: number;
    soloInProgress: number;
  } | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  // Group runs (group plan Phase 5) layered on the pairings.
  const { runs: groupRuns } = useGroupRuns(projectId);
  const { user } = useAuthKit();
  const currentUserEmail = user?.email ?? '';
  const launch = useMineZeroLaunch();
  const [creatingGroup, setCreatingGroup] = useState<{
    cohort: CohortEntityRow;
    pairing: PairingRow;
    assignedEmails: string[];
    blockedReasons: Map<string, string>;
  } | null>(null);
  const groupRunsByPairing = useMemo(() => {
    const m = new Map<string, GroupRunRowData[]>();
    for (const r of groupRuns) {
      const key = String(r.cohortAssignmentId);
      const list = m.get(key) ?? [];
      list.push(r);
      m.set(key, list);
    }
    return m;
  }, [groupRuns]);

  const connOnline = minetwinState.isActive;
  const visibleCohorts = useMemo(
    () => cohorts.filter((c) => showArchived || !c.archived),
    [cohorts, showArchived],
  );
  const archivedCount = cohorts.filter((c) => c.archived).length;

  /** Shared awaited-call wrapper: toast the server's reason on refusal. */
  async function run(
    cohortId: bigint | null,
    label: string,
    fn: (c: MinetwinConn) => Promise<unknown>,
    okToast: Omit<Toast, 'id'>,
  ): Promise<boolean> {
    const c = minetwinState.getConnection() as MinetwinConn | null;
    if (!c) {
      toast({ title: 'Not connected to MineTwin module', kind: 'bad' });
      return false;
    }
    if (cohortId != null) setBusyId(cohortId);
    try {
      await fn(c);
      toast(okToast);
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({ title: `${label} failed`, sub: msg.slice(0, 200), kind: 'bad' });
      return false;
    } finally {
      if (cohortId != null) setBusyId(null);
    }
  }

  async function handleCreate(name: string, memberEmails: string[]) {
    return run(
      null,
      'Create cohort',
      (c) => c.reducers.createCohort({ name, projectId, memberEmails }),
      {
        title: `Cohort "${name}" created`,
        sub:
          memberEmails.length > 0
            ? `${memberEmails.length} member${memberEmails.length === 1 ? '' : 's'} — assign an orientation from the Orientation Assignment page`
            : 'Empty roster — add members any time',
        kind: 'good',
      },
    );
  }

  async function handleRename(cohort: CohortEntityRow) {
    const name = (
      await dialogs.prompt({
        title: 'Rename cohort',
        label: 'Name',
        initial: cohort.name,
        confirmLabel: 'Rename',
      })
    )?.trim();
    if (!name || name === cohort.name) return;
    await run(
      cohort.cohortId,
      'Rename',
      (c) => c.reducers.renameCohort({ cohortId: cohort.cohortId, name }),
      { title: `Renamed to "${name}"`, kind: 'good' },
    );
  }

  async function handleSetMembers(cohort: CohortEntityRow, memberEmails: string[]) {
    const removed = cohort.members.filter((m) => !memberEmails.includes(m)).length;
    const added = memberEmails.filter((m) => !cohort.members.includes(m)).length;
    const ok = await run(
      cohort.cohortId,
      'Update members',
      (c) =>
        c.reducers.setCohortMembers({ cohortId: cohort.cohortId, memberEmails }),
      {
        title: `"${cohort.name}" roster updated`,
        sub:
          [
            added > 0 ? `${added} added (back-filled into active orientations)` : '',
            removed > 0
              ? `${removed} removed (their not-started cohort assignments cancelled)`
              : '',
          ]
            .filter(Boolean)
            .join(' · ') || 'No changes',
        kind: 'good',
      },
    );
    if (ok) setEditing(null);
  }

  async function handleArchive(cohort: CohortEntityRow) {
    // #191: when something is genuinely under way, "archive" is a choice
    // between ending those sessions and letting them finish — a confirm
    // cannot express that, so hand off to the dialog.
    const work = liveCohortWork({
      pairings: cohort.pairings,
      assignments: assignmentSlices,
      runs: cohort.pairings.flatMap(
        (p) => groupRunsByPairing.get(String(p.cohortAssignmentId)) ?? [],
      ),
    });
    if (work.total > 0) {
      setArchiving({
        cohort,
        liveRuns: work.liveRuns,
        soloInProgress: work.soloInProgress,
      });
      return;
    }

    // Nothing under way: archiving still cancels lobbies and not-started
    // rows on the module side (#177), so the one-liner says so.
    const live = cohort.pairings.filter((p) => p.storedStatus === 'Active').length;
    const cancels =
      live === 0
        ? 'It has no orientation assigned.'
        : `This clears ${live} orientation${live === 1 ? '' : 's'} still assigned to it: members who have not started are unassigned and any group lobby closes.`;
    if (
      !(await dialogs.confirm({
        title: `Archive "${cohort.name}"?`,
        body: `The roster freezes (no renames, member edits or new orientation assignments). ${cancels}`,
        confirmLabel: 'Archive',
      }))
    )
      return;
    await archiveNow(cohort, 'end');
  }

  /** Both archive paths (#191). `finish` keeps live rooms and started runs. */
  async function archiveNow(cohort: CohortEntityRow, mode: 'end' | 'finish') {
    const ok = await run(
      cohort.cohortId,
      mode === 'finish' ? 'Archive, let them finish' : 'Archive',
      (c) =>
        mode === 'finish'
          ? c.reducers.archiveCohortFinishLive({ cohortId: cohort.cohortId })
          : c.reducers.archiveCohort({ cohortId: cohort.cohortId }),
      {
        title: `"${cohort.name}" archived`,
        sub:
          mode === 'finish'
            ? 'Live groups and started orientations carry on — cancel them from Live Sessions when you need to.'
            : undefined,
        kind: 'good',
      },
    );
    if (ok) setArchiving(null);
  }

  async function handleCancelPairing(cohort: CohortEntityRow, pairing: PairingRow) {
    const orientationName =
      orientationNameById.get(pairing.orientationId) ?? pairing.orientationId;
    if (
      !(await dialogs.confirm({
        title: `Cancel "${orientationName}" for "${cohort.name}"?`,
        body: 'Members who have not started are unassigned; anyone mid-orientation keeps going; assignments this cohort adopted are handed back as individual ones.',
        confirmLabel: 'Cancel orientation',
        cancelLabel: 'Keep',
        danger: true,
      }))
    )
      return;
    await run(
      cohort.cohortId,
      'Cancel',
      (c) =>
        c.reducers.cancelCohortAssignment({
          cohortAssignmentId: pairing.cohortAssignmentId,
        }),
      { title: `"${orientationName}" cancelled for "${cohort.name}"`, kind: 'good' },
    );
  }

  async function handleCreateGroup(args: {
    name: string;
    memberEmails: string[];
    leaderEmail: string;
    modeTag: 'Managed' | 'Auto';
    allowLateJoin: boolean;
    autoStallSeconds: number;
  }) {
    if (!creatingGroup) return;
    const ok = await run(
      creatingGroup.cohort.cohortId,
      'Regroup',
      (c) =>
        c.reducers.createGroupRun({
          cohortAssignmentId: creatingGroup.pairing.cohortAssignmentId,
          name: args.name,
          memberEmails: args.memberEmails,
          leaderEmail: args.leaderEmail,
          mode: { tag: args.modeTag } as any,
          allowLateJoin: args.allowLateJoin,
          autoStallSeconds: args.autoStallSeconds,
        }),
      {
        title: `Group "${args.name}" created`,
        sub: `${args.memberEmails.length} member${args.memberEmails.length === 1 ? '' : 's'}, ${args.leaderEmail.split('@')[0]} leads (${args.modeTag}) — the shared session is provisioning; Start from the lobby when ready`,
        kind: 'good',
      },
    );
    if (ok) setCreatingGroup(null);
  }

  /** Status-transition reducers share one shape: id in, refusal reason out. */
  function runAction(
    label: string,
    okTitle: (r: GroupRunRowData) => string,
    call: (c: MinetwinConn, r: GroupRunRowData) => Promise<unknown>,
  ): (r: GroupRunRowData) => void {
    return (r) => {
      void run(null, label, (c) => call(c, r), { title: okTitle(r), kind: 'good' });
    };
  }

  const groupRunActions: GroupRunActions = {
    onStart: runAction('Start group', (r) => `"${r.name}" started`, (c, r) =>
      c.reducers.startGroupRun({ groupRunId: r.groupRunId }),
    ),
    onPause: runAction('Pause group', (r) => `"${r.name}" paused`, (c, r) =>
      c.reducers.pauseGroupRun({ groupRunId: r.groupRunId }),
    ),
    onResume: runAction('Resume group', (r) => `"${r.name}" resumed`, (c, r) =>
      c.reducers.resumeGroupRun({ groupRunId: r.groupRunId }),
    ),
    onUpdateSettings: (r, settings) => {
      void run(
        null,
        'Update group settings',
        (c) =>
          c.reducers.updateGroupRun({
            groupRunId: r.groupRunId,
            name: r.name,
            mode: { tag: r.modeTag } as any,
            allowLateJoin: settings.allowLateJoin,
            autoStallSeconds: settings.autoStallSeconds,
          }),
        {
          title: `"${r.name}" settings saved`,
          sub: `late joiners ${settings.allowLateJoin ? 'allowed' : 'closed'}${r.modeTag === 'Auto' ? ` · stall ${settings.autoStallSeconds === 0 ? 'never' : `${settings.autoStallSeconds}s`}` : ''}`,
          kind: 'good',
        },
      );
    },
    onHandOver: (r, newLeaderEmail) => {
      void run(
        null,
        'Hand over',
        (c) => c.reducers.takeOverGroupRun({ groupRunId: r.groupRunId, newLeaderEmail }),
        { title: `"${r.name}" leadership → ${newLeaderEmail.split('@')[0]}`, kind: 'good' },
      );
    },
    onCancelRun: async (r) => {
      if (
        !(await dialogs.confirm({
          title: `Cancel group "${r.name}"?`,
          body: 'The shared session closes. Every member carries on self-paced in a session of their own, keeping their progress.',
          confirmLabel: 'Cancel group',
          cancelLabel: 'Keep',
          danger: true,
        }))
      )
        return;
      void run(null, 'Cancel group', (c) => c.reducers.cancelGroupRun({ groupRunId: r.groupRunId }), {
        title: `"${r.name}" cancelled`,
        kind: 'good',
      });
    },
    onJoin: (r) => {
      void (async () => {
        const member = r.members[0];
        const iLead = isRunLeader(r.leaderEmail, currentUserEmail);
        if (r.sessionId === 0n || (!iLead && !member)) {
          toast({
            title: 'Cannot join yet',
            sub: r.sessionId === 0n ? 'The shared session is still provisioning.' : 'The run has no members.',
            kind: 'warn',
          });
          return;
        }
        // The leader is admitted as Participant by the module on connect and
        // must NOT record an observer grant (#168); everyone else observes.
        if (member && !iLead) {
          const ok = await run(
            null,
            'Observer join',
            (c) => c.reducers.joinSessionAsObserver({ assignmentId: member.assignmentId }),
            { title: `Joining "${r.name}" as observer`, sub: 'Launching the client in observer mode…', kind: 'good' },
          );
          if (!ok) return;
        } else {
          toast({ title: `Entering "${r.name}" as leader`, sub: 'Launching the client into the group session…', kind: 'good' });
        }
        const res = await launch.launchWithArgs(
          buildObserverLaunchArgs({ ...launch.argContext, sessionId: r.sessionId }),
        );
        if (!res.ok) {
          toast({
            title: iLead ? 'Launch pending' : 'Observer recorded, launch pending',
            sub: res.message ?? 'Launcher unreachable.',
            kind: 'warn',
          });
        }
      })();
    },
  };

  if (sitesReady && sites.length === 0) return <NoProjects pageName="Cohorts" />;
  if (membership.isReady && currentSite && !membership.isMember)
    return <NoSiteMembership pageName="Cohorts" siteName={currentSite.name} />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 style={{ margin: 0, fontSize: 18 }}>Cohorts</h1>
          <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>
            {visibleCohorts.length} on this site
            {archivedCount > 0 ? (
              <>
                {' · '}
                <button
                  type="button"
                  className="btn sm ghost"
                  style={{ padding: '0 6px', fontSize: 11 }}
                  onClick={() => setShowArchived((v) => !v)}
                >
                  {showArchived ? 'hide' : 'show'} {archivedCount} archived
                </button>
              </>
            ) : null}
            {currentSite ? <> · {currentSite.name}</> : null}
            {connOnline ? null : (
              <span style={{ color: 'var(--bad)' }}> · offline</span>
            )}
          </div>
        </div>
      </div>

      <div className="page-body">
        <CreateCohortCard
          key={projectId}
          userEmails={userEmails}
          busy={busyId !== null}
          onCreate={handleCreate}
        />

        <div className="card mt-14">
          <div className="card-head">
            <div>
              <h3>Rosters</h3>
              <div className="sub">
                {isReady
                  ? 'Newest first · assign an orientation from the Orientation Assignment page (target: Cohort)'
                  : 'Waiting for the cohort views — the module publish may not have landed on this org yet.'}
              </div>
            </div>
          </div>
          <div className="card-body no-pad">
            {!isReady ? (
              <Empty>Waiting for the cohort views…</Empty>
            ) : visibleCohorts.length === 0 ? (
              <Empty>No cohorts yet — name one above and pick its members.</Empty>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Cohort</th>
                    <th>Members</th>
                    <th>Orientations</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {visibleCohorts.map((c) => (
                    <CohortRow
                      key={String(c.cohortId)}
                      cohort={c}
                      expanded={expanded === String(c.cohortId)}
                      onToggle={() =>
                        setExpanded(
                          expanded === String(c.cohortId) ? null : String(c.cohortId),
                        )
                      }
                      busy={busyId === c.cohortId}
                      orientationNameById={orientationNameById}
                      assignmentSlices={assignmentSlices}
                      groupRunsByPairing={groupRunsByPairing}
                      currentUserEmail={currentUserEmail}
                      runActions={groupRunActions}
                      onCreateGroup={(p, assignedEmails, blockedReasons) =>
                        setCreatingGroup({ cohort: c, pairing: p, assignedEmails, blockedReasons })
                      }
                      onRename={() => void handleRename(c)}
                      onEditMembers={() => setEditing(c)}
                      onArchive={() => void handleArchive(c)}
                      onCancelPairing={(p) => void handleCancelPairing(c, p)}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {editing ? (
        <EditMembersDrawer
          cohort={editing}
          userEmails={userEmails}
          busy={busyId !== null}
          onCancel={() => setEditing(null)}
          onSave={(emails) => void handleSetMembers(editing, emails)}
        />
      ) : null}

      {archiving ? (
        <ArchiveCohortDialog
          cohortName={archiving.cohort.name}
          liveRuns={archiving.liveRuns}
          soloInProgress={archiving.soloInProgress}
          busy={busyId !== null}
          onCancel={() => setArchiving(null)}
          onArchiveEndSessions={() => void archiveNow(archiving.cohort, 'end')}
          onArchiveFinishLive={() => void archiveNow(archiving.cohort, 'finish')}
        />
      ) : null}

      {creatingGroup ? (
        <CreateGroupDrawer
          pairingLabel={`${creatingGroup.cohort.name} · ${
            orientationNameById.get(creatingGroup.pairing.orientationId) ??
            creatingGroup.pairing.orientationId
          }`}
          assignedEmails={creatingGroup.assignedEmails}
          blockedReasons={creatingGroup.blockedReasons}
          siteEmails={userEmails}
          currentUserEmail={currentUserEmail}
          busy={busyId !== null}
          onCancel={() => setCreatingGroup(null)}
          onCreate={(args) => void handleCreateGroup(args)}
        />
      ) : null}
    </>
  );
}
